import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress } from "@mui/material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import AdsAPIs from "../../apiRoutes/Ads";
import { GET_AGENCY_SPRINTS_QUERY_KEY } from "../../utils/constants/queryKeys";
import SectionHeader from "../common/SectionHeader";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { DatePicker } from "../ui/date-picker";
import CustomModal from "../common/CustomModal";
import { formatDisplayDate, formatDisplayDateTime } from "../../utils/helperFunctions";
import toast from "react-hot-toast";
import usePermissions from "../../hooks/usePermissions";
import { PERMISSION_CODES } from "../../utils/constants/permissions";
import useZustandStore from "../../zustandStore/useZustandStore";

const LIMIT = 20;

const AdSprintsSection = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(0);
  const [addSprintOpen, setAddSprintOpen] = useState(false);
  const [sprintFile, setSprintFile] = useState(null);
  const [deliveryDate, setDeliveryDate] = useState(null);
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canUpdateSprints = hasPermission(PERMISSION_CODES.AD_REQUEST_UPDATE);
  const isLoggedIn = !!useZustandStore((s) => s?.currentUser?.roles);

  useEffect(() => {
    if (searchParams.get("open") !== "sprint") return;
    const next = new URLSearchParams(searchParams);
    next.delete("open");
    setSearchParams(next, { replace: true });
    if (!isLoggedIn) return;
    if (canUpdateSprints) setAddSprintOpen(true);
  }, [searchParams, setSearchParams, canUpdateSprints, isLoggedIn]);

  const { mutateAsync: createSprint, isPending: creatingSprint } = useMutation({
    mutationFn: (payload) => AdsAPIs.createSprint({ axiosPrivate, payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GET_AGENCY_SPRINTS_QUERY_KEY });
      toast.success("Sprint created.");
      setAddSprintOpen(false);
      setSprintFile(null);
      setDeliveryDate(null);
    },
  });

  const handleAddSprint = async () => {
    if (!sprintFile) return toast.error("Upload a file for the sprint.");
    try {
      const assetUrl = await AdsAPIs.uploadFile({ axiosPrivate, file: sprintFile });
      if (!assetUrl) return toast.error("Upload failed.");
      await createSprint({
        filename: sprintFile.name,
        asset_url: assetUrl,
        delivery_date: deliveryDate ? format(deliveryDate, "yyyy-MM-dd") : undefined,
      });
    } catch (_) {
      toast.error("Create failed.");
    }
  };

  const { data: result, isLoading } = useQuery({
    queryKey: [...GET_AGENCY_SPRINTS_QUERY_KEY, page],
    queryFn: () =>
      AdsAPIs.listSprints({
        axiosPrivate,
        limit: LIMIT,
        offset: page * LIMIT,
      }),
  });

  const sprints = result?.data ?? [];
  const totalCount = result?.count ?? 0;

  return (
    <section className="p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <SectionHeader title="Ad Sprints" />
        {canUpdateSprints && (
          <Button onClick={() => setAddSprintOpen(true)}>Add sprint date</Button>
        )}
      </div>
      <CustomModal
        open={addSprintOpen}
        title="Add sprint date"
        onClose={() => { setAddSprintOpen(false); setSprintFile(null); setDeliveryDate(null); }}
        handleConfirmation={handleAddSprint}
        handleConfirmationBtnText="Create"
        isLoading={creatingSprint}
      >
        <div className="py-1 space-y-4">
          <div className="space-y-2">
            <Label>File</Label>
            <div className="flex gap-2 items-center flex-wrap">
              <label className="inline-flex h-9 items-center justify-center rounded-md border border-input bg-background px-3 text-sm font-medium shadow-sm hover:bg-accent hover:text-accent-foreground cursor-pointer">
                Choose file
                <input type="file" className="sr-only" onChange={(e) => setSprintFile(e.target.files?.[0] ?? null)} />
              </label>
              {sprintFile && <span className="text-sm text-gray-600 truncate max-w-[200px]" title={sprintFile.name}>{sprintFile.name}</span>}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Delivery date</Label>
            <DatePicker value={deliveryDate} onChange={setDeliveryDate} placeholder="Pick a date" />
          </div>
        </div>
      </CustomModal>
      <Paper
        elevation={0}
        sx={{
          borderRadius: "12px",
          border: "1px solid #e5e7eb",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          minHeight: "calc(100vh - 200px)",
        }}
      >
        {isLoading ? (
          <Box display="flex" alignItems="center" justifyContent="center" minHeight={320} gap={2}>
            <CircularProgress size={28} sx={{ color: "#7f7ad5" }} />
            <span className="text-[#7f7ad5] font-medium">Loading ad sprints…</span>
          </Box>
        ) : (
          <>
            <Box sx={{ flex: "1 1 auto", display: "flex", flexDirection: "column", minHeight: 0 }}>
              <TableContainer sx={{ flex: "1 1 auto", minHeight: 0 }}>
                <Table
                  size="medium"
                  sx={{
                    fontFamily: "'DM Sans', system-ui, sans-serif",
                    "& .MuiTableCell-root": {
                      fontFamily: "'DM Sans', system-ui, sans-serif",
                      padding: "16px",
                    },
                    "& .MuiTableBody-root .MuiTableCell-root": { borderBottom: "none" },
                    "& .MuiTableHead-root .MuiTableCell-root": {
                      borderLeft: "none",
                      borderRight: "none",
                      borderTop: "none",
                      backgroundColor: "#fff",
                    },
                  }}
                >
                  <TableHead>
                    <TableRow sx={{ borderBottom: "1px solid #e5e7eb", backgroundColor: "#fff" }}>
                      <TableCell sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#4b5563", border: "none" }}>Sprint ID</TableCell>
                      <TableCell sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#4b5563", border: "none" }}>Filename</TableCell>
                      <TableCell sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#4b5563", border: "none" }}>Delivery date</TableCell>
                      <TableCell sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#4b5563", border: "none" }}>Asset</TableCell>
                      <TableCell sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#4b5563", border: "none" }}>Created on</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sprints.length > 0 ? (
                      sprints.map((s) => (
                        <TableRow key={s.id} hover>
                          <TableCell sx={{ fontWeight: 500 }}>{s.id}</TableCell>
                          <TableCell sx={{ color: "text.secondary" }}>{s.filename || "—"}</TableCell>
                          <TableCell sx={{ color: "text.secondary" }}>{formatDisplayDate(s.delivery_date) ?? "—"}</TableCell>
                          <TableCell sx={{ color: "text.secondary" }}>
                            {s.asset_url ? (
                              <a
                                href={s.asset_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primaryPurple hover:underline font-medium text-sm break-all"
                              >
                                Open asset
                              </a>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                          <TableCell sx={{ color: "text.secondary" }}>{formatDisplayDateTime(s.created_on) ?? "—"}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 6, color: "text.secondary" }}>
                          No ad sprints found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-200 bg-muted/30 flex-shrink-0">
              <span className="text-sm text-muted-foreground">
                Showing {sprints.length ? page * LIMIT + 1 : 0}–{Math.min(page * LIMIT + sprints.length, totalCount)} of {totalCount}
              </span>
              <div className="flex gap-1.5">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="h-8">
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page * LIMIT + sprints.length >= totalCount}
                  onClick={() => setPage((p) => p + 1)}
                  className="h-8"
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </Paper>
    </section>
  );
};

export default AdSprintsSection;
